/**
 * The specification for the accounts-payable register a trainee builds in the
 * AI track — not the data itself.
 *
 * It used to be the data: twelve fixed invoices the trainee copied out of the
 * page and pasted into their prompt. That made the auto-checker easy (everyone
 * ended up with an identical sheet) and the exercise pointless. If you already
 * have the rows, pasting them into the sheet is quicker than asking an agent
 * to, and what the trainee learns is that the terminal is a slow clipboard.
 *
 * So the trainee now describes this shape instead, and Claude writes an Apps
 * Script that generates rows to fit it. Everyone ends up with a different
 * register, which is the point: the machine produced the data, and the thing
 * that produced it can be re-run, adjusted and handed to someone else.
 *
 * What that costs is fixed-value checking — nothing here can assert an amount
 * or a total any more. The checks assert structure instead (the right columns,
 * the right number of rows, amounts that are numbers, statuses from this list)
 * and self-consistency (a summary that agrees with the rows above it). That is
 * a better question to put to a trainee than "did you paste our block".
 */

export const AP_TAB_NAME = 'Invoices'
export const SUMMARY_TAB_NAME = 'Summary'
export const SUMMARY_HEADERS = ['Status', 'Total']
export const BAND_COLUMN_HEADER = 'Amount Band'

export const AP_HEADERS = ['Invoice', 'Vendor', 'Date', 'Amount', 'Status', 'Approver']

/** How many invoices the prompt asks for — enough to be worth automating. */
export const ROW_COUNT = 40

export const STATUSES = ['Paid', 'Unpaid', 'Disputed']

/**
 * Approvers are roles rather than names, and the consultancies are invented.
 *
 * Trainees paste this register into a sheet they share around and hand to an
 * LLM. An earlier version named real colleagues — including the actual Finance
 * Director — as the approver of a disputed invoice from a real firm, which is
 * indistinguishable from a genuine AP register once it leaves the training
 * context.
 */
export const APPROVERS = ['Sample Manager', 'Sample Director']

export const VENDORS = [
  'Officeworks', 'Telstra', 'Xero', 'Canva', 'Slack', 'Atlassian', 'Uber',
  'Amazon Web Services', 'Northwind Consulting', 'Example Advisory',
]

/** Amounts land inside this, so the band exercise has all three bands in it. */
export const AMOUNT_RANGE = { min: 50, max: 50000 }

/** Everything falls inside one quarter. */
export const DATE_RANGE = { from: '2026-07-01', to: '2026-09-30' }

/**
 * One deliberate duplicate: same vendor, same amount, different invoice
 * number, a few days apart. Later modules teach duplicate detection, and a
 * generated register with nothing planted in it has nothing to find.
 */
export const PLANTED_DUPLICATES = 1

/* ─── Module 19: the first ask, deliberately tiny ─── */

export const FIRST_ASK_TAB = 'Practice'
export const FIRST_ASK_VALUE = 'Claude was here'

/* ─── Amount bands, used once the register exists ─── */

/**
 * Listed largest first so `bandFor` can take the first match. The boundaries
 * are stated as ">= min" and the module copy says the same ("$10,000 and
 * over", "$1,000 to $9,999.99"), because a band whose wording and whose check
 * disagree at the edge would fail a trainee who read carefully.
 */
export const AMOUNT_BANDS = [
  { label: 'Large', min: 10000, describes: '$10,000 and over' },
  { label: 'Medium', min: 1000, describes: '$1,000 to $9,999.99' },
  { label: 'Small', min: 0, describes: 'under $1,000' },
]

export const BAND_LABELS = AMOUNT_BANDS.map((b) => b.label)

/** The band a given amount belongs in, or null if it's not a usable number. */
export function bandFor(amount) {
  const n = Number(amount)
  if (!Number.isFinite(n)) return null
  return AMOUNT_BANDS.find((b) => n >= b.min)?.label ?? null
}

/**
 * A few rows shown on the page so the trainee can see the shape they are
 * describing. Illustration only — nothing is checked against these, and every
 * trainee's real register will differ.
 */
export const SAMPLE_ROWS = [
  ['INV-1042', 'Officeworks', '2026-07-02', 1284.5, 'Paid', 'Sample Manager'],
  ['INV-1043', 'Northwind Consulting', '2026-07-09', 45000, 'Disputed', 'Sample Director'],
  ['INV-1044', 'Xero', '2026-07-15', 89, 'Paid', 'Sample Manager'],
  ['INV-1045', 'Telstra', '2026-08-03', 2450, 'Unpaid', 'Sample Manager'],
]

/** Formats like the sheet will: 1234.5 -> "1,234.50" */
export function money(n) {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
